import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { isIngestOwned, previewNormaliseTag, type BrandMediaItem } from '@/entities/brand-media';
import { usePermissions } from '@/entities/user';

import { useApplyTag } from '../model/useApplyTag';

/**
 * Names a tag and puts it on everything currently chosen in the grid.
 *
 * A tag cannot be created on its own: the only write the server takes is an asset's whole list for
 * one key, so a tag exists exactly as long as something carries it. Naming one is therefore the same
 * act as first using it, which is why this stands beside the rail's heading rather than inside it --
 * the rail reads what the library is tagged with, and what it would take to add to that is a
 * selection, which only the surface around the grid has. Once the tag is in the rail, the row's own
 * control is the cheaper way back to it.
 */
export const TagSelectedButton = ({
  rows,
  stale = false,
}: {
  rows: readonly BrandMediaItem[];
  /** Behind the server, so writing each row's rebuilt list would undo what overtook it. See `ApplyTagButton`. */
  stale?: boolean;
}) => {
  const { t } = useTranslation('tools');
  const { apply, isPending } = useApplyTag();
  const { canManageBrandAssets } = usePermissions();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const nothingChosen = rows.length === 0;
  const locked = nothingChosen || stale || !canManageBrandAssets;

  // Focused on opening rather than through `autoFocus`: this field appears in response to a click,
  // so moving focus to it is expected, where a page that grabs focus on load is not. Keyed on the
  // popover opening rather than done inline on the element, which would pull focus back out of the
  // value field on every keystroke.
  const keyRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) {
      keyRef.current?.focus();
    }
  }, [open]);

  const close = () => {
    setOpen(false);
    setKey('');
    setValue('');
    setError(null);
  };

  const submit = async () => {
    const tagKey = previewNormaliseTag(key);
    const tagValue = previewNormaliseTag(value);
    if (tagKey === '' || tagValue === '') {
      return;
    }
    if (isIngestOwned(tagKey)) {
      setError(t('brandMedia.reservedTagKey'));
      return;
    }

    await apply(rows, tagKey, tagValue);
    close();
  };

  const trigger = (
    <Button
      variant="ghost"
      size="icon"
      className="size-7 shrink-0"
      disabled={locked}
      aria-label={t('brandMedia.tagSelected')}
    >
      <PlusIcon className="size-4" />
    </Button>
  );

  return (
    // Its own provider rather than the rail's: a tooltip whose provider is missing throws rather
    // than degrading, and this control has to stand up in its own tests.
    <TooltipProvider>
      <Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
        <Tooltip>
          {/* The span carries the tooltip rather than the button: a disabled button fires no pointer
            events, and the tooltip is the only place the reason it is disabled is written. */}
          <TooltipTrigger asChild>
            <span className="inline-flex">
              {locked ? trigger : <PopoverTrigger asChild>{trigger}</PopoverTrigger>}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {!canManageBrandAssets
              ? t('brandMediaLibrary.permissions.noPermission')
              : nothingChosen
                ? t('brandMedia.tagSelectedNeedsSelection')
                : stale
                  ? t('brandMedia.tagsCatchingUp')
                  : t('brandMedia.tagSelected')}
          </TooltipContent>
        </Tooltip>

        <PopoverContent align="end" className="flex w-64 flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground">
            {t('brandMedia.tagSelectedTitle', { count: rows.length })}
          </p>
          <Input
            ref={keyRef}
            size="sm"
            value={key}
            placeholder={t('brandMedia.tagSelectedKey')}
            disabled={isPending}
            onChange={(event) => {
              setKey(event.target.value);
              setError(null);
            }}
          />
          <Input
            size="sm"
            value={value}
            placeholder={t('brandMedia.tagSelectedValue')}
            disabled={isPending}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void submit();
            }}
          />
          {error !== null && <p className="text-xs text-destructive">{error}</p>}
          <Button
            size="sm"
            className="self-end"
            disabled={isPending || key.trim() === '' || value.trim() === ''}
            onClick={() => void submit()}
          >
            {t('brandMedia.tagSelectedApply')}
          </Button>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};
